import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import ReceiptMoney from "@redotech/redo-icons/receipt-money.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as supportBillingAcceptanceModalCss from "./support-billing-acceptance-modal.module.css";

interface SupportBillingAcceptanceModalProps {
  children: ReactNode;
}

export const SupportBillingAcceptanceModal = memo(
  function SupportBillingAcceptanceModal(
    props: SupportBillingAcceptanceModalProps,
  ) {
    const { children } = props;
    const team = useRequiredContext(TeamContext);
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);

    const needsBillingAcceptance =
      !(
        team.settings.support?.billing?.base?.freeTrialEndDate &&
        new Date(team.settings.support?.billing?.base?.freeTrialEndDate) >
          new Date()
      ) &&
      (team.settings.support?.billing?.base?.pricePerMonthInCents || 0) > 0 &&
      (!team.settings.support?.billing?.base?.subscriptionActive ||
        !team.settings?.shopifySubscription?.[0]);

    const thresholdCheck = useLoad(async () => {
      if (!needsBillingAcceptance || !team) {
        return false;
      }

      const result = await merchantRpcClient.hasReachedConversationThreshold();

      return result.hasReachedThreshold;
    }, [merchantRpcClient, team, needsBillingAcceptance]);

    useEffect(() => {
      if (thresholdCheck.value) {
        setShowModal(true);
      }
    }, [thresholdCheck.value]);

    const handleAcceptBilling = () => {
      navigate(`/stores/${team._id}/settings/billing?currentTab=support`);
    };

    const handleCallClick = () => {
      window.open(
        "https://calendly.com/aaron-moise-getredo/30min?month=2025-07",
        "_blank",
      );
    };

    if (showModal) {
      return (
        <Flex dir="column" height="full" position="relative" w="full">
          <Flex height="full" overflow="auto" position="relative" w="full">
            {children}
          </Flex>
          <div className={supportBillingAcceptanceModalCss.blockedOverlay}>
            <div className={supportBillingAcceptanceModalCss.blockedMessage}>
              <Flex align="center" dir="column" gap="lg">
                <Icon
                  arbiterIconSvg={ReceiptMoney}
                  color="warning"
                  fontSize="xl"
                />
                <Text fontSize="lg" fontWeight="semibold">
                  You're out of free credits
                </Text>
                <Text>
                  In order to continue using Redo's support tool you have to opt
                  into billing.
                </Text>
                <Flex gap="md">
                  <RedoButton
                    hierarchy="secondary"
                    onClick={handleCallClick}
                    size="sm"
                    text="Book a call"
                  />
                  <RedoButton
                    hierarchy="primary"
                    onClick={handleAcceptBilling}
                    size="sm"
                    text="Opt in to billing"
                  />
                </Flex>
                <Text fontSize="sm">
                  Read about the price breakdown{" "}
                  <a
                    className={supportBillingAcceptanceModalCss.hereLink}
                    href="https://help.getredo.com/en/articles/11673216-ai-cx-platform-pricing"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    here
                  </a>
                  .
                </Text>
              </Flex>
            </div>
          </div>
        </Flex>
      );
    }

    return <>{children}</>;
  },
);
